import { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  ChevronRight, 
  ChevronDown, 
  Users, 
  MoreVertical, 
  Trash2, 
  Move,
  Plus,
  FolderOpen,
  Folder,
  Briefcase,
  Loader2,
  Edit
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { AddGroupDialog } from './AddGroupDialog';
import { EditGroupDialog } from './EditGroupDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { 
  getGroupHierarchy, 
  setSubgroupSection
} from '@/lib/firebase/queries/hierarchical-groups';

import { getGroupMembers } from '@/lib/firebase/queries/group-members';
import { deleteGroup } from '@/lib/firebase/queries/groups';
import { patchGroupNode, removeGroupKeepingChildren } from '@/lib/groups/groupTree';
import { isSectionDeleteBlocked, moveGroupNode, sectionMoveTargets } from '@/lib/groups/sectionDelete';
import { toast } from 'sonner';
import type { Group } from '@/types/support';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { OrganizationGroupingModal } from './OrganizationGroupingModal';

interface HierarchicalGroupListProps {
  organizationId: string;
  searchQuery: string;
  onRefresh?: () => void;
}

type GroupNode = Group & { 
  children: GroupNode[];
  level?: number;
  members?: number;
  directMembers?: number;
  aggregatedMembers?: number;
};



export function HierarchicalGroupList({ 
  organizationId, 
  searchQuery, 
  onRefresh 
}: HierarchicalGroupListProps) {
  const { user } = useAuth();
  const { t, dir } = useLanguage();
  // Force refresh: v2
  const [groups, setGroups] = useState<GroupNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [selectedGroupToEdit, setSelectedGroupToEdit] = useState<GroupNode | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<GroupNode | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Subgroup being moved out of the section in the delete dialog.
  const [movingSubgroupId, setMovingSubgroupId] = useState<string | null>(null);
  const [showManageMembersModal, setShowManageMembersModal] = useState(false);
  const [selectedGroupForMembers, setSelectedGroupForMembers] = useState<GroupNode | null>(null);
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);

  
  // Real-time sync management
  const syncCleanupRef = useRef<(() => void) | null>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (organizationId) {
      fetchGroupHierarchy();
      
      // Set up real-time synchronization (disabled for now)
      setupRealTimeSync();
    }
    
    // Cleanup on unmount
    return () => {
      if (syncCleanupRef.current) {
        syncCleanupRef.current();
        syncCleanupRef.current = null;
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [organizationId]);

  // ✅ OPTIMIZED: Granular updates instead of full reloads
  const updateSingleGroup = async (groupId: string, reason: string) => {
    console.log(`🎯 Updating single group ${groupId}: ${reason}`);
    
    try {
      // Find and update only the specific group
      const updatedGroups = await Promise.all(
        groups.map(async (group) => {
          if (group.id === groupId || hasChildGroup(group, groupId)) {
            // Only update the affected group/section and its member count
            const { getGroupMemberCountVirtual } = await import('@/lib/firebase/queries/virtual-membership');
            const memberCount = await getGroupMemberCountVirtual(group.id);
            
            return {
              ...group,
              members: memberCount,
              children: await updateChildrenMemberCounts(group.children, groupId)
            };
          }
          return group; // No change needed for unaffected groups
        })
      );
      
      setGroups(updatedGroups);
    } catch (error) {
      console.warn('⚠️ Failed granular update, falling back to full refresh:', error);
      fetchGroupHierarchy(); // Fallback only on error
    }
  };

  // Helper to check if a group contains a specific child
  const hasChildGroup = (group: GroupNode, targetId: string): boolean => {
    if (group.children) {
      return group.children.some(child => 
        child.id === targetId || hasChildGroup(child, targetId)
      );
    }
    return false;
  };

  // Helper to update member counts in children
  const updateChildrenMemberCounts = async (children: GroupNode[], targetId: string): Promise<GroupNode[]> => {
    if (!children) return [];
    
    return Promise.all(
      children.map(async (child) => {
        if (child.id === targetId) {
          const { getGroupMemberCountVirtual } = await import('@/lib/firebase/queries/virtual-membership');
          const memberCount = await getGroupMemberCountVirtual(child.id);
          return { ...child, members: memberCount };
        }
        return {
          ...child,
          children: await updateChildrenMemberCounts(child.children, targetId)
        };
      })
    );
  };

  // Keep manual refresh for major changes only (new group creation, etc.)
  const manualRefresh = (reason: string) => {
    console.log(`🔄 Full refresh (major change): ${reason}`);
    fetchGroupHierarchy();
  };



  const setupRealTimeSync = async () => {
    // ✅ COMPLETELY DISABLED: Real-time sync removed to prevent excessive reloads
    console.log(`🚫 Real-time sync DISABLED - Using manual updates only for better performance`);
      
      // Simple cleanup function (no active listeners)
      syncCleanupRef.current = () => {
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
          refreshTimeoutRef.current = null;
        }
      };
  };

  // Helper function to collect all parent group IDs for auto-expansion
  const collectParentGroupIds = (nodes: GroupNode[]): string[] => {
    const parentIds: string[] = [];
    
    const traverse = (groups: GroupNode[]) => {
      for (const group of groups) {
        // If this is a parent group (section), add it to the list
        if (group.groupType === 'parent') {
          parentIds.push(group.id);
        }
        // Recursively check children
        if (group.children && group.children.length > 0) {
          traverse(group.children);
        }
      }
    };
    
    traverse(nodes);
    return parentIds;
  };

  const fetchGroupHierarchy = async () => {
    try {
      setLoading(true);
      const hierarchy = await getGroupHierarchy(organizationId);
      
      // Add aggregated member counts and level information
      const enrichedHierarchy = await enrichGroupHierarchy(hierarchy, 0);
      setGroups(enrichedHierarchy);
      
      // Auto-expand all sections (parent groups) by default
      const parentGroupIds = collectParentGroupIds(enrichedHierarchy);
      setExpandedGroups(new Set(parentGroupIds));
    } catch (error) {
      console.error('Error fetching group hierarchy:', error);
      
      // If it's a Firebase index error, provide more helpful feedback
      if (error.message && error.message.includes('requires an index')) {
        console.warn('Firebase index not available, falling back to empty state');
        setGroups([]); // Set empty array to show "no groups" message
      } else {
        toast.error(t('dashboard.manager.groups.fetchError'));
      }
    } finally {
      setLoading(false);
    }
  };



  const enrichGroupHierarchy = async (
    nodes: GroupNode[], 
    level: number
  ): Promise<GroupNode[]> => {
    const enriched: GroupNode[] = [];
    
    for (const node of nodes) {
      // Recursively process children
      const enrichedChildren = await enrichGroupHierarchy(node.children, level + 1);
      
      // Use virtual membership system - works for both groups and sections
      const { getGroupMemberCountVirtual } = await import('@/lib/firebase/queries/virtual-membership');
      const memberCount = await getGroupMemberCountVirtual(node.id);
      
      enriched.push({
        ...node,
        children: enrichedChildren,
        level,
        // Use real member count from groupMembers collection
        members: memberCount,
        directMembers: memberCount,
        aggregatedMembers: memberCount
      });
    }
    
    return enriched;
  };

  const toggleGroupExpansion = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const handleEditGroup = async (group: GroupNode) => {
    setSelectedGroupToEdit(group);
    setEditDialogOpen(true);
  };

  const handleDeleteGroup = (group: GroupNode) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };



  // Owner decision #4: empty a section before deleting it. Each subgroup
  // goes to another section of the organization, or out of any section.
  const moveSubgroupOut = async (subgroupId: string, targetSectionId: string | null) => {
    if (!groupToDelete) return;
    try {
      setMovingSubgroupId(subgroupId);
      await setSubgroupSection(subgroupId, targetSectionId);
      setGroups(prev => moveGroupNode(prev, subgroupId, targetSectionId));
      setGroupToDelete(prev =>
        prev ? { ...prev, children: prev.children.filter(c => c.id !== subgroupId) } : prev
      );
      toast.success(t('dashboard.manager.groups.moveSuccess'));
    } catch (error) {
      console.error('Error moving subgroup out of section:', error);
      toast.error(t('dashboard.manager.groups.moveError'));
    } finally {
      setMovingSubgroupId(null);
    }
  };

  const confirmDeleteGroup = async () => {
    if (!groupToDelete || !user) return;
    if (isSectionDeleteBlocked(groupToDelete)) return;

    try {
      setDeleting(true);
      const success = await deleteGroup(groupToDelete.id, user.uid);
      // deleteGroup reports failure (permission, network) as false, not a throw.
      if (!success) {
        toast.error(t('dashboard.manager.groups.deleteError'));
        return;
      }
      
      toast.success(t('dashboard.manager.groups.deleteSuccess'));
      setDeleteDialogOpen(false);
      
      // Remove it locally. A deleted section's subgroups are not deleted
      // with it: they move to the top level, as a reload would show them.
      setGroups(prevGroups => removeGroupKeepingChildren(prevGroups, groupToDelete.id));
      setGroupToDelete(null);
      onRefresh?.();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error(t('dashboard.manager.groups.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const handleManageMembers = (group: GroupNode) => {
    setSelectedGroupForMembers(group);
    setShowManageMembersModal(true);
  };

  const getPossibleParents = async (excludeGroupId: string): Promise<GroupNode[]> => {
    const possibleParents: GroupNode[] = [];
    
    const collectGroups = (nodes: GroupNode[]) => {
      for (const node of nodes) {
        if (node.id !== excludeGroupId && !isDescendant(node, excludeGroupId)) {
          possibleParents.push(node);
        }
        collectGroups(node.children);
      }
    };
    
    collectGroups(groups);
    
    return possibleParents;
  };

  const isDescendant = (potentialAncestor: GroupNode, targetId: string): boolean => {
    for (const child of potentialAncestor.children) {
      if (child.id === targetId || isDescendant(child, targetId)) {
        return true;
      }
    }
    return false;
  };

  const filterGroups = (nodes: GroupNode[], query: string): GroupNode[] => {
    if (!query.trim()) return nodes;
    
    return nodes.filter(node => {
      const matchesQuery = node.name.toLowerCase().includes(query.toLowerCase()) ||
                          node.description.toLowerCase().includes(query.toLowerCase());
      const hasMatchingChildren = filterGroups(node.children, query).length > 0;
      
      if (matchesQuery || hasMatchingChildren) {
        return {
          ...node,
          children: hasMatchingChildren ? filterGroups(node.children, query) : node.children
        };
      }
      return false;
    }).map(node => ({
      ...node,
      children: filterGroups(node.children, query)
    }));
  };

  const renderGroupNode = (group: GroupNode) => {
    const hasChildren = group.children.length > 0;
    const isExpanded = expandedGroups.has(group.id);
    const paddingStart = (group.level || 0) * 24;

    return (
      <div key={group.id} className="space-y-2">
        <Card className={cn(
          "transition-all duration-200",
          group.level === 0 
            ? "border-s-4 border-s-primary"
            : "border-s-2 border-s-muted"
        )}>
          <CardContent className="p-4">
            <div 
              className="flex items-center gap-3"
              style={{ paddingInlineStart: paddingStart }}
              dir={dir}
            >
              {/* Expand/Collapse Button */}
              {hasChildren ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => toggleGroupExpansion(group.id)}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              ) : (
                <div className="w-6" />
              )}

              {/* Group Icon */}
              {group.level === 0 ? (
                <Briefcase className="h-5 w-5 text-primary" />
              ) : hasChildren ? (
                isExpanded ? (
                  <FolderOpen className="h-5 w-5 text-primary" />
                ) : (
                  <Folder className="h-5 w-5 text-primary" />
                )
              ) : (
                <Users className="h-5 w-5 text-muted-foreground" />
              )}

              {/* Group Info */}
              <div className="flex-1 min-w-0">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium truncate">{group.name}</h3>
                    {group.groupType === 'parent' && (
                      <Badge className="inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-slate-200 text-slate-700">
                        {t('dashboard.manager.groups.section')}
                      </Badge>
                    )}
                    {group.groupType === 'subgroup' && (
                      <Badge className="inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-secondary text-secondary-foreground">
                        {t('dashboard.manager.groups.group')}
                      </Badge>
                    )}
                  </div>
                  {group.description && (
                    <p className="text-sm text-muted-foreground truncate">
                      {group.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Member Count */}
              <div className="flex items-center gap-2">
                <div className="text-center">
                  <div className="text-sm font-medium">
                    {/* ✨ Use virtual membership count from enrichGroupHierarchy */}
                    {group.members || 0}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {hasChildren ? t('dashboard.manager.groups.totalMembers') : t('dashboard.manager.groups.members')}
                  </div>
                </div>
              </div>

              {/* Actions Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align={dir === 'rtl' ? 'start' : 'end'} dir={dir}>
                  {/* Manage Members - now available for both groups and sections */}
                  <DropdownMenuItem onClick={() => handleManageMembers(group)} className="gap-2">
                    <Users className="h-4 w-4" />
                        {t('dashboard.manager.groups.actions.manageMembers')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                  
                  {/* Edit Group/Section */}
                  <DropdownMenuItem onClick={() => handleEditGroup(group)} className="gap-2">
                    <Edit className="h-4 w-4" />
                    {group.groupType === 'parent' ? t('dashboard.manager.groups.editSection') : t('dashboard.manager.groups.editGroup')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                  
                  {/* Debug option for sections */}

                  
                  <DropdownMenuItem 
                    onClick={() => handleDeleteGroup(group)}
                    className="text-destructive gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    {group.groupType === 'parent' ? t('dashboard.manager.groups.deleteSection') : t('dashboard.manager.groups.deleteGroup')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardContent>
        </Card>

        {/* Render Children */}
        {hasChildren && isExpanded && (
          <div className="space-y-2">
            {group.children.map(child => renderGroupNode(child))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin mr-2" />
        <span className="text-muted-foreground">
          {t('dashboard.manager.groups.loading')}
        </span>
      </div>
    );
  }

  const filteredGroups = filterGroups(groups, searchQuery);

  return (
    <div className="space-y-4" dir={dir}>
      {/* Header with Create Group Button */}
      <div className="flex items-center justify-end">
        <Button 
          onClick={() => setShowAddGroupDialog(true)}
          className="bg-[#7dda55] hover:bg-[#6dc447] text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('dashboard.manager.groups.addGroup')}
        </Button>
      </div>

      {filteredGroups.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchQuery 
                ? t('dashboard.manager.groups.noGroupsFound')
                : t('dashboard.manager.groups.noGroups')
              }
            </h3>
            <p className="text-muted-foreground">
              {searchQuery 
                ? t('dashboard.manager.groups.tryDifferentSearch')
                : t('dashboard.manager.groups.createFirstGroup')
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        filteredGroups.map(group => renderGroupNode(group))
      )}

      {/* Edit Group Dialog */}
      {selectedGroupToEdit && (
        <EditGroupDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          group={selectedGroupToEdit}
          onSuccess={(changes) => {
          setEditDialogOpen(false);
          if (selectedGroupToEdit) {
            // Show the saved name/description now (an edit changes no counts).
            setGroups(prev => patchGroupNode(prev, selectedGroupToEdit.id, changes));
          }
          setSelectedGroupToEdit(null);
          onRefresh?.();
        }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent dir={dir}>
          <AlertDialogHeader>
            <AlertDialogTitle>
            {groupToDelete?.groupType === 'parent' ? t('dashboard.manager.groups.deleteSectionTitle') : t('dashboard.manager.groups.deleteGroupTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {groupToDelete?.groupType === 'parent' 
                ? t('dashboard.manager.groups.deleteSectionDescription', { sectionName: groupToDelete?.name })
                : t('dashboard.manager.groups.deleteGroupDescription', { groupName: groupToDelete?.name })
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          {groupToDelete && isSectionDeleteBlocked(groupToDelete) && (
            <div className="space-y-3" data-testid="section-delete-blocked">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
                {t('dashboard.manager.groups.sectionNotEmpty', { count: groupToDelete.children.length })}
                {' '}
                <strong>{t('dashboard.manager.groups.moveOrDeleteFirst')}</strong>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {groupToDelete.children.map(child => {
                  const targets = sectionMoveTargets(groups, groupToDelete.id, child.id);
                  const busy = movingSubgroupId === child.id;
                  return (
                    <div key={child.id} className="flex flex-wrap items-center gap-2 p-2 border rounded" data-testid={`section-child-${child.id}`}>
                      <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                      <span className="flex-1 min-w-[8rem] truncate font-medium">{child.name}</span>
                      <Select
                        disabled={busy || targets.length === 0}
                        onValueChange={(sectionId) => moveSubgroupOut(child.id, sectionId)}
                      >
                        <SelectTrigger className="w-44 h-8 text-xs" aria-label={t('dashboard.manager.groups.moveToSection')}>
                          <SelectValue placeholder={targets.length === 0 ? t('dashboard.manager.groups.noOtherSections') : t('dashboard.manager.groups.moveToSection')} />
                        </SelectTrigger>
                        <SelectContent dir={dir}>
                          {targets.map(section => (
                            <SelectItem key={section.id} value={section.id}>{section.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        disabled={busy}
                        onClick={() => moveSubgroupOut(child.id, null)}
                      >
                        {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : t('dashboard.manager.groups.noSection')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteGroup}
              disabled={deleting || isSectionDeleteBlocked(groupToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting && <Loader2 className={`h-4 w-4 animate-spin ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />}
              {groupToDelete?.groupType === 'parent' ? t('dashboard.manager.groups.deleteSection') : t('dashboard.manager.groups.deleteGroup')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage Members Modal */}
      {selectedGroupForMembers && (
        <OrganizationGroupingModal
          open={showManageMembersModal}
          onOpenChange={(open) => {
            setShowManageMembersModal(open);
          if (!open && selectedGroupForMembers) {
            // ✅ OPTIMIZED: Only update the specific group that had member changes
            updateSingleGroup(selectedGroupForMembers.id, 'member management closed');
            }
          }}
          organizationId={selectedGroupForMembers.organization || organizationId}
          groups={[{
            id: selectedGroupForMembers.id,
            name: selectedGroupForMembers.name,
            description: selectedGroupForMembers.description,
            categories: selectedGroupForMembers.categories,
            isPrivate: selectedGroupForMembers.isPrivate,
            organization: selectedGroupForMembers.organization,
            groupType: selectedGroupForMembers.groupType,
            members: selectedGroupForMembers.members,
            createdAt: selectedGroupForMembers.createdAt,
            updatedAt: selectedGroupForMembers.updatedAt,
            creatorId: selectedGroupForMembers.creatorId,
            creatorName: selectedGroupForMembers.creatorName,
            lastPost: selectedGroupForMembers.lastPost
          }]}
        />
      )}

      {/* Add Group Dialog */}
      <AddGroupDialog
        open={showAddGroupDialog}
        onOpenChange={setShowAddGroupDialog}
        onSuccess={() => {
          setShowAddGroupDialog(false);
          manualRefresh('new group created');
        }}
        organizationId={organizationId}
      />
    </div>
  );
}